import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { frDate, EVENT_STATUS } from "@/lib/constants";
import { Truck, CalendarDays, CheckCircle2, Circle, LogOut } from "lucide-react";
import { SonarGrid } from "@/components/ui/sonar-grid";

export default function ScreenDisplay() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [slide, setSlide] = useState(0);
  const [clock, setClock] = useState(new Date());

  const load = async () => {
    const token = localStorage.getItem("eventos_token");
    try {
      const { data } = await axios.get(`${API}/screen/display`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) { console.debug("Screen refresh failed, keeping last data", e?.message); }
  };

  useEffect(() => {
    load();
    const dataT = setInterval(load, 30000);
    const slideT = setInterval(() => setSlide((s) => (s + 1) % 2), 14000);
    const clockT = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(dataT); clearInterval(slideT); clearInterval(clockT); };
  }, []);

  const color = data?.brand_color || "#FF6A00";
  const events = data?.loading?.events || [];
  const week = data?.week || [];

  return (
    <SonarGrid gradient={["#FF6A00", "#FFD500"]} interactive={false} spacing={34} baseOpacity={0.18} className="min-h-screen bg-[#09090b] p-8 text-white">
      <div className="mb-8 flex items-center justify-between border-b border-zinc-800 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl font-display text-2xl font-bold text-black" style={{ background: color }}>
            {(data?.org_name || "E").slice(0, 1)}
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold">{data?.org_name || "HalgarOS"}</h1>
            <p className="text-xl capitalize text-zinc-400">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-display text-5xl font-bold tabular-nums">{clock.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
          </div>
          <button onClick={logout} className="rounded-full border border-zinc-800 p-3 text-zinc-600 hover:text-white" title="Déconnexion"><LogOut className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-center gap-3">
        {["Chargement du jour", "Planning de la semaine"].map((t, i) => (
          <div key={t} className={`flex items-center gap-2 rounded-full px-5 py-2 text-lg font-semibold transition-all ${slide === i ? "text-white" : "text-zinc-600"}`} style={slide === i ? { background: `${color}22` } : {}}>
            {i === 0 ? <Truck className="h-5 w-5" /> : <CalendarDays className="h-5 w-5" />}
            {t}
          </div>
        ))}
      </div>

      {slide === 0 && (
        <div>
          {events.length === 0 ? (
            <Empty label="Rien à charger aujourd'hui" />
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {events.map((ev) => {
                const done = ev.loaded_count === ev.total_count;
                return (
                  <div key={ev.event_id} className={`rounded-2xl border p-6 ${done ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-800 bg-zinc-900"}`}>
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="font-display text-2xl font-bold">{ev.event_title}</h2>
                      <span className={`rounded-full px-3 py-1 text-lg font-bold ${done ? "bg-emerald-500/20 text-emerald-400" : "text-black"}`} style={done ? {} : { background: color }}>{ev.loaded_count}/{ev.total_count}</span>
                    </div>
                    <div className="space-y-2">
                      {ev.items.map((it) => {
                        const loaded = it.status === "charge" || it.status === "retourne";
                        return (
                          <div key={it.res_id} className="flex items-center gap-3 text-xl">
                            {loaded ? <CheckCircle2 className="h-7 w-7 text-emerald-400" /> : <Circle className="h-7 w-7 text-zinc-600" />}
                            <span className={loaded ? "text-emerald-300 line-through" : "text-white"}>{it.item_name}{it.location ? <span className="ml-2 rounded-full bg-[#FF6A00]/15 px-2 py-0.5 text-sm text-[#FF6A00]">{it.location}</span> : null}</span>
                            <span className="ml-auto font-bold text-zinc-400">×{it.quantity}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {slide === 1 && (
        <div>
          {week.length === 0 ? (
            <Empty label="Aucun événement cette semaine" />
          ) : (
            <div className="space-y-3">
              {week.map((e, i) => {
                const st = EVENT_STATUS[e.status] || EVENT_STATUS.prospect;
                return (
                  <div key={i} className="flex items-center gap-6 rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
                    <div className="w-40 text-center">
                      <p className="font-display text-2xl font-bold" style={{ color }}>{new Date(e.date).toLocaleDateString("fr-FR", { weekday: "short" })}</p>
                      <p className="text-4xl font-bold">{new Date(e.date).getDate()}</p>
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-2xl font-bold">{e.title}</p>
                      <p className="text-xl text-zinc-400">{e.contact_name || "—"} · {e.category}</p>
                    </div>
                    <span className={`rounded-full border px-4 py-2 text-lg font-semibold ${st.cls}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </SonarGrid>
  );
}

function Empty({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center">
      <CalendarDays className="h-20 w-20 text-zinc-700" />
      <p className="mt-6 font-display text-3xl font-bold text-zinc-500">{label}</p>
    </div>
  );
}
